/**
 * mSTEM Open Project Modal container (PRJ-02 / LLD UC 07).
 *
 * Owns local state for the search box and the "currently loading" row id.
 * Sorts the redux-stored myProjects list by updatedAt DESC, filters by the
 * 200ms-debounced search term (case-insensitive substring match against
 * name + description), and renders rows with a per-row "Tải dự án" button.
 *
 * Load handler (DP-02 + DP-05):
 *   - entry.vmState present (string)  → vm.loadProject(vmState) — real load
 *   - entry.vmState null (mock entry) → vm.loadProject(defaultProject()) —
 *                                       fake-load per DP-05
 *   - Both paths resolve to: setProjectTitle + setCurrentProject + success
 *     toast (variant differs: 'projectLoaded' vs 'projectLoadedMock'), then
 *     close the modal. On reject: 'projectLoadError' toast + clear
 *     loadingId.
 *
 * Search debounce cleanup (T-03-03-03 mitigation):
 *   componentWillUnmount cancels the lodash.debounce wrapper so React 16
 *   doesn't warn about setState on unmounted component after the 200ms
 *   window if user closes the modal mid-typing.
 *
 * W-9 fix: intl propType omitted — only the inner presentational
 * component (after injectIntl) consumes intl. The container is wrapped by
 * injectIntl for compose() symmetry / future intl-aware logic only.
 */
import bindAll from 'lodash.bindall';
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {injectIntl} from 'react-intl';
import VM from 'openblock-vm';

import {closeOpenProjectModal} from '../reducers/modals';
import {setCurrentProject} from '../reducers/my-projects';
import {setProjectTitle} from '../reducers/project-title';
import {showAlertWithTimeout} from '../reducers/alerts';
import defaultProject from '../lib/default-project';
import {getProjectDetail} from '../lib/mstem-mock/project-details';
import log from '../lib/log';

import OpenProjectModalComponent from '../components/open-project-modal/open-project-modal.jsx';

const SEARCH_DEBOUNCE_MS = 200;

const sortByUpdatedDesc = function (list) {
    return list.slice().sort((a, b) => {
        const ta = new Date(a && a.updatedAt ? a.updatedAt : 0).getTime();
        const tb = new Date(b && b.updatedAt ? b.updatedAt : 0).getTime();
        return tb - ta;
    });
};

const filterProjects = function (list, term) {
    const t = (term || '').trim().toLowerCase();
    if (!t) return list;
    return list.filter(p => {
        const inName = (p.name || '').toLowerCase().indexOf(t) >= 0;
        const inDesc = (p.description || '').toLowerCase().indexOf(t) >= 0;
        return inName || inDesc;
    });
};

class OpenProjectModal extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleSearch',
            'applySearch',
            'handleLoad',
            'handleDetail',
            'handleCloseDetail',
            'handleLoadDetail',
            'handleClose'
        ]);
        this.applySearchDebounced = debounce(this.applySearch, SEARCH_DEBOUNCE_MS);
        this.state = {
            searchTerm: '',
            appliedTerm: '',
            loadingId: null,
            loadingError: false,
            // PRJ-02 S-2: id of the row whose ℹ was clicked (null = list view).
            detailId: null
        };
    }
    componentWillUnmount () {
        // MICRO-3.1 BUG FIX (Bug 2): Mark unmounted so any still-in-flight
        // vm.loadProject promise from a "Tải dự án" click does NOT dispatch
        // alerts/setState after the user has already closed the modal. This
        // eliminates the "stale toast appearing after close" surface that
        // could surface a false projectLoadError or projectLoaded toast.
        this._isUnmounted = true;
        if (this.applySearchDebounced && this.applySearchDebounced.cancel) {
            this.applySearchDebounced.cancel();
        }
    }
    handleSearch (e) {
        const value = e.target.value;
        this.setState({searchTerm: value});
        this.applySearchDebounced(value);
    }
    applySearch (value) {
        this.setState({appliedTerm: value});
    }
    handleLoad (id) {
        if (this.state.loadingId) return;
        const entry = this.props.projects.find(p => p.id === id);
        if (!entry) {
            this.setState({loadingError: true});
            this.props.onShowLoadError();
            return;
        }
        this.setState({loadingId: id, loadingError: false});

        const onSuccess = () => {
            // MICRO-3.1 BUG FIX (Bug 2): guard against late resolution after the
            // modal has been closed — do not dispatch alerts or call setState.
            if (this._isUnmounted) return;
            this.props.onSetProjectTitle(entry.name);
            this.props.onSetCurrent(entry.id);
            if (entry.vmState) {
                this.props.onShowLoadedAlert();
            } else {
                this.props.onShowLoadedMockAlert();
            }
            this.setState({loadingId: null});
            this.props.onClose();
        };
        const onError = err => {
            log.warn('OpenProjectModal vm.loadProject error', err);
            // MICRO-3.1 BUG FIX (Bug 2): guard against late rejection after the
            // modal has been closed — do NOT pop the error toast in that case.
            if (this._isUnmounted) return;
            this.props.onShowLoadError();
            this.setState({loadingId: null, loadingError: true});
        };

        try {
            if (entry.vmState) {
                // Real saved project — feed serialized vmState directly to VM.
                this.props.vm.loadProject(entry.vmState).then(onSuccess, onError);
            } else {
                // DP-05 fake-load: reset workspace to default Scratch cat
                // project. User sees the title change + toast confirming the
                // (mock) project was opened.
                // MICRO-3.1 BUG FIX: defaultProject() returns an asset LIST,
                // but vm.loadProject expects a JSON string/buffer of just the
                // project. Pass assets[0].data (the project JSON string) so the
                // mock "Tải" path actually resolves instead of always rejecting
                // with a "Không thể tải dự án" toast for every mock-row click.
                const assets = defaultProject();
                const projectJson = assets && assets[0] && assets[0].data;
                this.props.vm.loadProject(projectJson).then(onSuccess, onError);
            }
        } catch (err) {
            onError(err);
        }
    }
    handleDetail (row) {
        if (!row || !row.id) return;
        this.setState({detailId: row.id});
    }
    handleCloseDetail () {
        this.setState({detailId: null});
    }
    handleLoadDetail () {
        // "Tải dự án" inside the detail view reuses the list load path, so the
        // fake-load / real-vmState branching and the toasts stay in one place.
        if (this.state.detailId) this.handleLoad(this.state.detailId);
    }
    handleClose () {
        this.props.onClose();
    }
    render () {
        const sorted = sortByUpdatedDesc(this.props.projects);
        const filtered = filterProjects(sorted, this.state.appliedTerm);
        // Merge list fields + detail-only mock metadata only when the detail
        // view is open (see src/lib/mstem-mock/project-details.js for why they
        // are stored apart). Unknown ids yield {} → the modal renders '—'.
        const entry = this.state.detailId ?
            this.props.projects.find(p => p.id === this.state.detailId) : null;
        const detailProject = entry ?
            Object.assign({}, entry, getProjectDetail(entry.id)) : null;
        return (
            <OpenProjectModalComponent
                detailProject={detailProject}
                filteredProjects={filtered}
                hasProjects={this.props.projects.length > 0}
                loadingError={this.state.loadingError}
                loadingId={this.state.loadingId}
                searchTerm={this.state.searchTerm}
                onClose={this.handleClose}
                onCloseDetail={this.handleCloseDetail}
                onDetail={this.handleDetail}
                onLoad={this.handleLoad}
                onLoadDetail={this.handleLoadDetail}
                onSearch={this.handleSearch}
            />
        );
    }
}

OpenProjectModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSetCurrent: PropTypes.func.isRequired,
    onSetProjectTitle: PropTypes.func.isRequired,
    onShowLoadError: PropTypes.func.isRequired,
    onShowLoadedAlert: PropTypes.func.isRequired,
    onShowLoadedMockAlert: PropTypes.func.isRequired,
    projects: PropTypes.arrayOf(PropTypes.object).isRequired, // eslint-disable-line react/forbid-prop-types
    vm: PropTypes.instanceOf(VM).isRequired
};

const mapStateToProps = state => ({
    projects: state.scratchGui.myProjects.list,
    vm: state.scratchGui.vm
});

const mapDispatchToProps = dispatch => ({
    onClose: () => dispatch(closeOpenProjectModal()),
    onSetCurrent: id => dispatch(setCurrentProject(id)),
    onSetProjectTitle: title => dispatch(setProjectTitle(title)),
    onShowLoadedAlert: () => showAlertWithTimeout(dispatch, 'projectLoaded'),
    onShowLoadedMockAlert: () => showAlertWithTimeout(dispatch, 'projectLoadedMock'),
    onShowLoadError: () => showAlertWithTimeout(dispatch, 'projectLoadError')
});

export default compose(
    injectIntl,
    connect(mapStateToProps, mapDispatchToProps)
)(OpenProjectModal);
